import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { pushNavigation } from "../../redux/slices/navigationSlice";
import {
  LogOut,
  FileText,
  Edit,
  TrendingUp,
  Palette,
  FileImage,
  ShoppingCart,
  Package,
  Factory,
  Users,
  UserCheck,
  Briefcase,
  CheckCircle,
  DollarSign,
  UserCog,
  Store,
  Handshake,
  Shield,
  Wrench,
  ClipboardList,
  Eye,
  Check,
  X,
  AlertCircle,
  Download,
} from "lucide-react";
import "./dashboard.css";
import ScreenHeader from "../../components/ScreenHeader";
import Pagination from "../../components/Pagination";

export default function DashboardMD({ user, onLogout }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("pending-po");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [showPODetails, setShowPODetails] = useState(false);
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const [showDrawingDetails, setShowDrawingDetails] = useState(false);
  const [currentPOPage, setCurrentPOPage] = useState(1);
  
  // Customer Selection Popup States
  const [showCustomerPopup, setShowCustomerPopup] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  
  const itemsPerPage = 10;

  // Sample Customer Data for Popup
  const [customers] = useState([
    { id: "CUST-001", name: "ABC Corporation", contact: "John Smith", email: "john@abccorp.com", phone: "+91 98765 43210", address: "123, Industrial Area, Mumbai - 400001", gstin: "27AABCT1234C1Z5" },
    { id: "CUST-002", name: "XYZ Ltd", contact: "Jane Doe", email: "jane@xyzltd.com", phone: "+91 87654 32109", address: "456, Business Park, Delhi - 110001", gstin: "07XYZL5678D2Z6" },
    { id: "CUST-003", name: "PQR Industries", contact: "Bob Wilson", email: "bob@pqrind.com", phone: "+91 76543 21098", address: "789, Tech Zone, Bangalore - 560001", gstin: "29PQRI9012E3Z7" },
    { id: "CUST-004", name: "LMN Enterprises", contact: "Alice Brown", email: "alice@lmnent.com", phone: "+91 65432 10987", address: "321, Trade Center, Chennai - 600001", gstin: "33LMNE3456F4Z8" },
    { id: "CUST-005", name: "DEF Corp", contact: "Mike Davis", email: "mike@defcorp.com", phone: "+91 54321 09876", address: "567, Export Zone, Kolkata - 700001", gstin: "19DEFC7890G5Z9" }
  ]);

  // Sample Pending PO Data
  const [pendingPOs, setPendingPOs] = useState([
    {
      id: "PO-001",
      poNumber: "PO-2024-001",
      vendor: "ABC Suppliers Pvt Ltd",
      date: "2024-03-15",
      amount: "₹1,25,000",
      status: "Pending",
      department: "Purchase",
      items: [
        { name: "Raw Material A", quantity: 100, price: "₹500", total: "₹50,000" },
        { name: "Raw Material B", quantity: 50, price: "₹1,500", total: "₹75,000" }
      ],
      remarks: "Urgent requirement for production"
    },
    {
      id: "PO-002",
      poNumber: "PO-2024-002",
      vendor: "XYZ Enterprises",
      date: "2024-03-14",
      amount: "₹85,000",
      status: "Pending",
      department: "Production",
      items: [
        { name: "Machine Parts", quantity: 10, price: "₹8,500", total: "₹85,000" }
      ],
      remarks: "Need approval for maintenance"
    },
    {
      id: "PO-003",
      poNumber: "PO-2024-003",
      vendor: "Global Metals",
      date: "2024-03-13",
      amount: "₹98,500",
      status: "Pending",
      department: "Store",
      items: [
        { name: "Steel Sheets", quantity: 75, price: "₹1,100", total: "₹82,500" },
        { name: "Fasteners", quantity: 200, price: "₹80", total: "₹16,000" }
      ],
      remarks: "Store stock replenishment"
    },
    {
      id: "PO-004",
      poNumber: "PO-2024-004",
      vendor: "Prime Plastics",
      date: "2024-03-12",
      amount: "₹42,000",
      status: "Pending",
      department: "Production",
      items: [
        { name: "Plastic Components", quantity: 120, price: "₹350", total: "₹42,000" }
      ],
      remarks: "Raw materials for assembly"
    },
    {
      id: "PO-005",
      poNumber: "PO-2024-005",
      vendor: "Elite Electronics",
      date: "2024-03-11",
      amount: "₹68,400",
      status: "Pending",
      department: "Quality",
      items: [
        { name: "Sensors", quantity: 60, price: "₹1,140", total: "₹68,400" }
      ],
      remarks: "Calibration test equipment"
    },
    {
      id: "PO-006",
      poNumber: "PO-2024-006",
      vendor: "North Star Logistics",
      date: "2024-03-10",
      amount: "₹23,500",
      status: "Pending",
      department: "Purchase",
      items: [
        { name: "Packaging Material", quantity: 200, price: "₹117.5", total: "₹23,500" }
      ],
      remarks: "Packaging refill"
    },
    {
      id: "PO-007",
      poNumber: "PO-2024-007",
      vendor: "Apex Bearings",
      date: "2024-03-09",
      amount: "₹74,800",
      status: "Pending",
      department: "Maintenance",
      items: [
        { name: "Ball Bearings", quantity: 160, price: "₹467.5", total: "₹74,800" }
      ],
      remarks: "Maintenance replacement"
    },
    {
      id: "PO-008",
      poNumber: "PO-2024-008",
      vendor: "Spectrum Chemicals",
      date: "2024-03-08",
      amount: "₹51,200",
      status: "Pending",
      department: "Production",
      items: [
        { name: "Industrial Solvent", quantity: 40, price: "₹1,280", total: "₹51,200" }
      ],
      remarks: "Cleaning compound order"
    },
    {
      id: "PO-009",
      poNumber: "PO-2024-009",
      vendor: "Bright Power Solutions",
      date: "2024-03-07",
      amount: "₹38,750",
      status: "Pending",
      department: "Store",
      items: [
        { name: "Battery Pack", quantity: 25, price: "₹1,550", total: "₹38,750" }
      ],
      remarks: "Backup power components"
    },
    {
      id: "PO-010",
      poNumber: "PO-2024-010",
      vendor: "Nexa Controls",
      date: "2024-03-06",
      amount: "₹92,300",
      status: "Pending",
      department: "Production",
      items: [
        { name: "Control Modules", quantity: 35, price: "₹2,637", total: "₹92,300" }
      ],
      remarks: "Automation upgrade parts"
    },
    {
      id: "PO-011",
      poNumber: "PO-2024-011",
      vendor: "Vertex Instruments",
      date: "2024-03-05",
      amount: "₹54,900",
      status: "Pending",
      department: "Quality",
      items: [
        { name: "Inspection Tools", quantity: 30, price: "₹1,830", total: "₹54,900" }
      ],
      remarks: "Quality control instrument order"
    },
    {
      id: "PO-012",
      poNumber: "PO-2024-012",
      vendor: "Summit Hardware",
      date: "2024-03-04",
      amount: "₹67,200",
      status: "Pending",
      department: "Maintenance",
      items: [
        { name: "Tool Kits", quantity: 40, price: "₹1,680", total: "₹67,200" }
      ],
      remarks: "Workshop tool replacement"
    },
    {
      id: "PO-013",
      poNumber: "PO-2024-013",
      vendor: "Astra Fabrics",
      date: "2024-03-03",
      amount: "₹39,600",
      status: "Pending",
      department: "Production",
      items: [
        { name: "Protective Covers", quantity: 120, price: "₹330", total: "₹39,600" }
      ],
      remarks: "Safety cover production"
    },
    {
      id: "PO-014",
      poNumber: "PO-2024-014",
      vendor: "Vertex Instruments",
      date: "2024-03-02",
      amount: "₹28,750",
      status: "Pending",
      department: "Purchase",
      items: [
        { name: "Measurement Gauges", quantity: 25, price: "₹1,150", total: "₹28,750" }
      ],
      remarks: "Measurement tools for inspection"
    },
    {
      id: "PO-015",
      poNumber: "PO-2024-015",
      vendor: "Greenline Electronics",
      date: "2024-03-01",
      amount: "₹81,850",
      status: "Pending",
      department: "Production",
      items: [
        { name: "Circuit Boards", quantity: 50, price: "₹1,637", total: "₹81,850" }
      ],
      remarks: "Production electronics batch"
    }
  ]);

  const totalPOPages = Math.ceil(pendingPOs.length / itemsPerPage);
  const currentPOs = pendingPOs.slice((currentPOPage - 1) * itemsPerPage, currentPOPage * itemsPerPage);

  useEffect(() => {
    if (currentPOPage > totalPOPages) {
      setCurrentPOPage(totalPOPages || 1);
    }
  }, [currentPOPage, totalPOPages]);

  // Sample Edit Request Data
  const [editRequests, setEditRequests] = useState([
    {
      id: "ER-001",
      requestNumber: "ER-2024-001",
      requester: "Purchase Manager",
      department: "Purchase",
      requestDate: "2024-03-10",
      poNumber: "PO-2024-001",
      fieldToEdit: "Quantity",
      oldValue: "100 units",
      newValue: "150 units",
      reason: "Production demand increased",
      status: "Pending",
      priority: "High"
    },
    {
      id: "ER-002",
      requestNumber: "ER-2024-002",
      requester: "Store Manager",
      department: "Store",
      requestDate: "2024-03-12",
      poNumber: "PO-2024-002",
      fieldToEdit: "Delivery Date",
      oldValue: "2024-03-20",
      newValue: "2024-03-18",
      reason: "Urgent requirement",
      status: "Pending",
      priority: "Medium"
    },
    {
      id: "ER-003",
      requestNumber: "ER-2024-003",
      requester: "Production Manager",
      department: "Production",
      requestDate: "2024-03-13",
      poNumber: "PO-2024-003",
      fieldToEdit: "Price",
      oldValue: "₹500",
      newValue: "₹475",
      reason: "Better rate negotiated",
      status: "Pending",
      priority: "Low"
    }
  ]);

  // Profit Loss Data
  const [profitLossData] = useState({
    creditor: {
      total: "₹5,25,000",
      invoices: [
        { invNumber: "INV-001", customer: "ABC Corp", amount: "₹1,50,000", date: "2024-03-01" },
        { invNumber: "INV-002", customer: "XYZ Ltd", amount: "₹2,00,000", date: "2024-03-05" },
        { invNumber: "INV-003", customer: "PQR Industries", amount: "₹1,75,000", date: "2024-03-10" }
      ]
    },
    debitor: {
      total: "₹4,50,000",
      grns: [
        { grnNumber: "GRN-001", vendor: "ABC Suppliers", amount: "₹1,25,000", date: "2024-03-02" },
        { grnNumber: "GRN-002", vendor: "XYZ Enterprises", amount: "₹1,50,000", date: "2024-03-06" },
        { grnNumber: "GRN-003", vendor: "Tech Solutions", amount: "₹1,75,000", date: "2024-03-11" }
      ]
    },
    profitLoss: "₹75,000"
  });

  // Pending Drawing Data
  const [pendingDrawings, setPendingDrawings] = useState([
    {
      id: "DRW-001",
      customer: "ABC Corp",
      drawingNumber: "DRG-2024-001",
      title: "Machine Part Design",
      uploadDate: "2024-03-10",
      status: "Pending",
      priority: "High",
      files: [
        { name: "design_v1.pdf", size: "2.5 MB", type: "PDF" },
        { name: "technical_specs.pdf", size: "1.8 MB", type: "PDF" }
      ]
    },
    {
      id: "DRW-002",
      customer: "XYZ Ltd",
      drawingNumber: "DRG-2024-002",
      title: "Product Blueprint",
      uploadDate: "2024-03-12",
      status: "Pending",
      priority: "Medium",
      files: [
        { name: "blueprint_v2.pdf", size: "3.2 MB", type: "PDF" },
        { name: "dimensions.pdf", size: "1.2 MB", type: "PDF" },
        { name: "specifications.xlsx", size: "0.8 MB", type: "Excel" }
      ]
    }
  ]);

  // List of Drawing Data (Approved)
  const [listOfDrawings, setListOfDrawings] = useState([
    {
      id: "DRW-003",
      customer: "PQR Industries",
      drawingNumber: "DRG-2024-003",
      title: "Assembly Drawing",
      uploadDate: "2024-03-05",
      approvalDate: "2024-03-08",
      status: "Approved",
      files: [
        { name: "assembly_drawing.pdf", size: "4.1 MB", type: "PDF" },
        { name: "parts_list.pdf", size: "1.5 MB", type: "PDF" },
        { name: "assembly_instructions.pdf", size: "2.0 MB", type: "PDF" }
      ]
    },
    {
      id: "DRW-004",
      customer: "ABC Corp",
      drawingNumber: "DRG-2024-004",
      title: "Quality Control Chart",
      uploadDate: "2024-03-01",
      approvalDate: "2024-03-04",
      status: "Approved",
      files: [
        { name: "qc_chart.pdf", size: "1.2 MB", type: "PDF" }
      ]
    }
  ]);

  // Sidebar menu items
  const tabItems = [
    { id: "pending-po", label: "Pending PO", icon: <FileText size={18} /> },
    { id: "edit-request", label: "Edit Request", icon: <Edit size={18} /> },
    { id: "profit-loss", label: "Profit Loss", icon: <TrendingUp size={18} /> },
    { id: "pending-drawing", label: "Pending Drawing", icon: <Palette size={18} /> },
    { id: "list-of-drawing", label: "List of Drawing", icon: <FileImage size={18} /> }
  ];

  // Department access buttons with routes (ALL 15 DEPARTMENTS)
  const departmentAccess = [
    { id: "general-manager", label: "General Manager", icon: <UserCog size={20} />, color: "#8B5CF6", route: "/gmdashboard" },
    { id: "purchase", label: "Purchase", icon: <Package size={20} />, color: "#10B981", route: "/purchase" },
    { id: "production", label: "Production", icon: <Factory size={20} />, color: "#F59E0B", route: "/pmdashboard" },
    { id: "sales", label: "Sales", icon: <ShoppingCart size={20} />, color: "#4F46E5", route: "/sales" },
    { id: "marketing", label: "Marketing", icon: <FileText size={20} />, color: "#EC4899", route: "/marketing-crm" },
    { id: "quality", label: "Quality", icon: <CheckCircle size={20} />, color: "#F59E0B", route: "/qualitymanagement" },
    { id: "accounts", label: "Accounts", icon: <DollarSign size={20} />, color: "#10B981", route: "/AccountsManager" },
    { id: "hr", label: "HR", icon: <Users size={20} />, color: "#EC4899", route: "/hr-dashboard" },
    { id: "employee", label: "Employee", icon: <Briefcase size={20} />, color: "#14B8A6", route: "/employee-dashboard" },
    { id: "supervisor", label: "Supervisor", icon: <UserCheck size={20} />, color: "#EF4444", route: "/supervisor-dashboard" },
    { id: "store", label: "Store", icon: <Store size={20} />, color: "#8B5CF6", route: "/store-dashboard" },
    { id: "customer", label: "Customer", icon: <Handshake size={20} />, color: "#06B6D4", route: "/customer-dashboard" },
    { id: "maintenance", label: "Maintenance", icon: <Wrench size={20} />, color: "#F97316", route: "/maintenance-dashboard" },
    { id: "security", label: "Security", icon: <Shield size={20} />, color: "#6B7280", route: "/security-dashboard" },
    { id: "ndp", label: "NDP", icon: <ClipboardList size={20} />, color: "#A855F7", route: "/ndp-dashboard" }
  ];

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    onLogout();
    navigate('/login');
  };
  const handleCancelLogout = () => setShowLogoutModal(false);

  // Handle department click
  const handleDepartmentClick = (department) => {
    if (department.id === "customer") {
      setSelectedDepartment(department);
      setSelectedCustomerId("");
      setShowCustomerPopup(true);
    } else {
      dispatch(pushNavigation({
        from: '/managing-director',
        to: department.route,
        fromName: 'MD Dashboard',
        toName: department.label
      }));
      navigate(department.route);
    }
  };

  // Handle customer selection and navigation
  const handleCustomerSelect = () => {
    if (selectedCustomerId) {
      const customer = customers.find(c => c.id === selectedCustomerId);
      dispatch(pushNavigation({
        from: '/managing-director',
        to: `${selectedDepartment.route}?customerId=${selectedCustomerId}`,
        fromName: 'MD Dashboard',
        toName: `${selectedDepartment.label} - ${customer.name}`
      }));
      navigate(`${selectedDepartment.route}?customerId=${selectedCustomerId}`);
      setShowCustomerPopup(false);
      setSelectedCustomerId("");
    } else {
      alert("Please select a customer to continue");
    }
  };

  // PO Functions
  const handleApprovePO = (poId) => {
    setPendingPOs(pendingPOs.map(po => 
      po.id === poId ? { ...po, status: "Approved" } : po
    ));
    alert(`PO ${poId} has been approved successfully!`);
  };

  const handleRejectPO = (poId) => {
    setPendingPOs((prevPOs) => {
      const updatedPOs = prevPOs.filter(po => po.id !== poId);
      const updatedTotalPages = Math.ceil(updatedPOs.length / itemsPerPage) || 1;
      setCurrentPOPage((currentPage) => Math.min(currentPage, updatedTotalPages));
      return updatedPOs;
    });
    alert(`PO ${poId} has been rejected.`);
  };

  const handleViewPODetails = (po) => {
    setSelectedPO(po);
    setShowPODetails(true);
  };

  const handleEditPO = (poId) => {
    alert(`Edit functionality for PO ${poId} - Opening edit form`);
  };

  // Edit Request Functions
  const handleApproveEditRequest = (requestId) => {
    setEditRequests(editRequests.map(req => 
      req.id === requestId ? { ...req, status: "Approved" } : req
    ));
    alert(`Edit Request ${requestId} has been approved!`);
  };

  const handleRejectEditRequest = (requestId) => {
    setEditRequests(editRequests.filter(req => req.id !== requestId));
    alert(`Edit Request ${requestId} has been rejected.`);
  };

  // Drawing Functions
  const handleApproveDrawing = (drawingId) => {
    const drawing = pendingDrawings.find(d => d.id === drawingId);
    setPendingDrawings(pendingDrawings.filter(d => d.id !== drawingId));
    setListOfDrawings([...listOfDrawings, { ...drawing, status: "Approved", approvalDate: new Date().toISOString().split('T')[0] }]);
    alert(`Drawing ${drawingId} has been approved!`);
  };

  const handleRejectDrawing = (drawingId) => {
    setPendingDrawings(pendingDrawings.filter(d => d.id !== drawingId));
    alert(`Drawing ${drawingId} has been rejected.`);
  };

  const handleViewDrawingDetails = (drawing) => {
    setSelectedDrawing(drawing);
    setShowDrawingDetails(true);
  };

  const handleDownloadFile = (fileName) => {
    alert(`Downloading file: ${fileName}`);
  };

  // Button styles mapping - NO hover effects
  const buttonStyles = {
    view: {
      backgroundColor: '#E0E7FF',
      color: '#4F46E5',
    },
    edit: {
      backgroundColor: '#FEF3C7',
      color: '#D97706',
    },
    approve: {
      backgroundColor: '#D1FAE5',
      color: '#059669',
    },
    reject: {
      backgroundColor: '#FEE2E2',
      color: '#DC2626',
    }
  };

  // Action Button Component with Tooltip - NO HOVER EFFECTS
  const ActionButton = ({ onClick, type, icon: Icon, tooltip }) => {
    const style = buttonStyles[type];
    return (
      <div style={{ position: 'relative', display: 'inline-flex' }}>
        <button
          onClick={onClick}
          style={{
            padding: '6px',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: style.backgroundColor,
            color: style.color,
            transition: 'none',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = style.backgroundColor;
            e.currentTarget.style.color = style.color;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = style.backgroundColor;
            e.currentTarget.style.color = style.color;
          }}
        >
          <Icon size={16} />
        </button>
        <span style={{
          position: 'absolute',
          bottom: 'calc(100% + 8px)',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#1F2937',
          color: '#ffffff',
          padding: '6px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          opacity: 0,
          visibility: 'hidden',
          zIndex: 1001,
          pointerEvents: 'none',
        }} className="tooltip-span">
          {tooltip}
        </span>
      </div>
    );
  };

  // Pending PO Content Component
  const PendingPOContent = () => (
    <div className="pending-po-container">
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon" style={{ background: "#4F46E5" }}>
            <FileText size={24} color="white" />
          </div>
          <div className="summary-info">
            <h3>{pendingPOs.length}</h3>
            <p>Total Pending PO</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon" style={{ background: "#10B981" }}>
            <DollarSign size={24} color="white" />
          </div>
          <div className="summary-info">
            <h3>₹{pendingPOs.reduce((sum, po) => sum + parseInt(po.amount.replace(/[^0-9]/g, '')), 0).toLocaleString()}</h3>
            <p>Total Amount</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon" style={{ background: "#F59E0B" }}>
            <AlertCircle size={24} color="white" />
          </div>
          <div className="summary-info">
            <h3>{pendingPOs.length}</h3>
            <p>Awaiting Action</p>
          </div>
        </div>
      </div>

      <div className="po-table-container">
        <table className="po-table">
          <thead>
            <tr><th>PO Number</th><th>Vendor</th><th>Date</th><th>Amount</th><th>Department</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {currentPOs.map((po) => (
              <tr key={po.id}>
                <td className="po-number">{po.poNumber}</td><td>{po.vendor}</td><td>{po.date}</td><td className="amount">{po.amount}</td><td>{po.department}</td>
                <td><span className="status-badge pending">{po.status}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <ActionButton onClick={() => handleViewPODetails(po)} type="view" icon={Eye} tooltip="View" />
                    <ActionButton onClick={() => handleEditPO(po.id)} type="edit" icon={Edit} tooltip="Edit" />
                    <ActionButton onClick={() => handleApprovePO(po.id)} type="approve" icon={Check} tooltip="Approve" />
                    <ActionButton onClick={() => handleRejectPO(po.id)} type="reject" icon={X} tooltip="Reject" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          currentPage={currentPOPage}
          totalPages={totalPOPages}
          onPageChange={setCurrentPOPage}
          pageSize={itemsPerPage}
          totalItems={pendingPOs.length}
        />
      </div>

      {/* PO Details Modal */}
      {showPODetails && selectedPO && (
        <div className="modal-overlay" onClick={() => setShowPODetails(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>PO Details - {selectedPO.poNumber}</h2><button className="modal-close" onClick={() => setShowPODetails(false)}>×</button></div>
            <div className="modal-body">
              <div className="details-grid">
                <div className="detail-item"><label>Vendor:</label><span>{selectedPO.vendor}</span></div>
                <div className="detail-item"><label>Date:</label><span>{selectedPO.date}</span></div>
                <div className="detail-item"><label>Amount:</label><span className="amount">{selectedPO.amount}</span></div>
                <div className="detail-item"><label>Department:</label><span>{selectedPO.department}</span></div>
                <div className="detail-item"><label>Status:</label><span className="status-badge pending">{selectedPO.status}</span></div>
                <div className="detail-item full-width"><label>Remarks:</label><span>{selectedPO.remarks}</span></div>
              </div>
              <div className="items-table"><h3>Items List</h3>
                <table><thead><tr><th>Item Name</th><th>Quantity</th><th>Unit Price</th><th>Total</th></tr></thead>
                  <tbody>{selectedPO.items.map((item, idx) => (<tr key={idx}><td>{item.name}</td><td>{item.quantity}</td><td>{item.price}</td><td>{item.total}</td></tr>))}</tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-approve" onClick={() => { handleApprovePO(selectedPO.id); setShowPODetails(false); }}><Check size={16} /> Approve</button>
              <button className="btn-reject" onClick={() => { handleRejectPO(selectedPO.id); setShowPODetails(false); }}><X size={16} /> Reject</button>
              <button className="btn-cancel" onClick={() => setShowPODetails(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Edit Request Content Component
  const EditRequestContent = () => (
    <div className="pending-po-container">
      <div className="summary-cards">
        <div className="summary-card"><div className="summary-icon" style={{ background: "#F59E0B" }}><Edit size={24} color="white" /></div><div className="summary-info"><h3>{editRequests.length}</h3><p>Total Edit Requests</p></div></div>
        <div className="summary-card"><div className="summary-icon" style={{ background: "#4F46E5" }}><AlertCircle size={24} color="white" /></div><div className="summary-info"><h3>{editRequests.filter(r => r.priority === "High").length}</h3><p>High Priority</p></div></div>
        <div className="summary-card"><div className="summary-icon" style={{ background: "#10B981" }}><CheckCircle size={24} color="white" /></div><div className="summary-info"><h3>{editRequests.filter(r => r.status === "Approved").length}</h3><p>Approved</p></div></div>
      </div>

      <div className="po-table-container">
        <table className="po-table">
          <thead><tr><th>Request #</th><th>Requester</th><th>Department</th><th>PO Number</th><th>Field to Edit</th><th>Old Value</th><th>New Value</th><th>Priority</th><th>Actions</th></tr></thead>
          <tbody>
            {editRequests.map((req) => (
              <tr key={req.id}>
                <td className="po-number">{req.requestNumber}</td><td>{req.requester}</td><td>{req.department}</td><td>{req.poNumber}</td><td>{req.fieldToEdit}</td><td>{req.oldValue}</td><td className="amount">{req.newValue}</td>
                <td><span className={`status-badge ${req.priority === "High" ? "pending" : req.priority === "Medium" ? "approved" : ""}`}>{req.priority}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <ActionButton onClick={() => handleApproveEditRequest(req.id)} type="approve" icon={Check} tooltip="Approve" />
                    <ActionButton onClick={() => handleRejectEditRequest(req.id)} type="reject" icon={X} tooltip="Reject" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Profit Loss Content Component
  const ProfitLossContent = () => (
    <div className="profit-loss-container">
      <div className="pl-summary">
        <div className="pl-card creditor">
          <h3>Creditor Grand Total</h3>
          <div className="pl-amount">{profitLossData.creditor.total}</div>
          <div className="pl-details">
            <h4>List Of Invoice Details (sort by invoice number wise)</h4>
            <table className="pl-table">
              <thead><tr><th>Invoice #</th><th>Customer Details</th><th>Amount</th><th>Date</th></tr></thead>
              <tbody>
                {profitLossData.creditor.invoices.map((inv, idx) => (
                  <tr key={idx}><td>{inv.invNumber}</td><td>{inv.customer}</td><td className="amount">{inv.amount}</td><td>{inv.date}</td></tr>
                ))}
                <tr className="total-row"><td colSpan="2"><strong>Customer Details Total</strong></td><td className="amount"><strong>{profitLossData.creditor.total}</strong></td><td></td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="pl-card debitor">
          <h3>Debitor Grand Total</h3>
          <div className="pl-amount">{profitLossData.debitor.total}</div>
          <div className="pl-details">
            <h4>List Of GRN Details (sort by GRN number wise)</h4>
            <table className="pl-table">
              <thead><tr><th>GRN #</th><th>Vendor Details</th><th>Amount</th><th>Date</th></tr></thead>
              <tbody>
                {profitLossData.debitor.grns.map((grn, idx) => (
                  <tr key={idx}><td>{grn.grnNumber}</td><td>{grn.vendor}</td><td className="amount">{grn.amount}</td><td>{grn.date}</td></tr>
                ))}
                <tr className="total-row"><td colSpan="2"><strong>Vendor Details Total</strong></td><td className="amount"><strong>{profitLossData.debitor.total}</strong></td><td></td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="pl-result">
        <h3>Profit / Loss</h3>
        <div className="pl-formula">Debitor Grand Total - Creditor Grand Total</div>
        <div className={`pl-value ${parseInt(profitLossData.profitLoss.replace(/[^0-9]/g, '')) >= 0 ? 'profit' : 'loss'}`}>
          {profitLossData.profitLoss}
        </div>
        <div className="pl-status">{parseInt(profitLossData.profitLoss.replace(/[^0-9]/g, '')) >= 0 ? 'PROFIT' : 'LOSS'}</div>
      </div>
    </div>
  );

  // Pending Drawing Content Component
  const PendingDrawingContent = () => (
    <div className="pending-po-container">
      <div className="summary-cards">
        <div className="summary-card"><div className="summary-icon" style={{ background: "#F59E0B" }}><Palette size={24} color="white" /></div><div className="summary-info"><h3>{pendingDrawings.length}</h3><p>Pending Drawings</p></div></div>
        <div className="summary-card"><div className="summary-icon" style={{ background: "#4F46E5" }}><AlertCircle size={24} color="white" /></div><div className="summary-info"><h3>{pendingDrawings.filter(d => d.priority === "High").length}</h3><p>High Priority</p></div></div>
      </div>

      <div className="po-table-container">
        <table className="po-table">
          <thead><tr><th>Customer</th><th>Drawing #</th><th>Title</th><th>Upload Date</th><th>Priority</th><th>Files</th><th>Actions</th></tr></thead>
          <tbody>
            {pendingDrawings.map((drawing) => (
              <tr key={drawing.id}>
                <td className="po-number">{drawing.customer}</td><td>{drawing.drawingNumber}</td><td>{drawing.title}</td><td>{drawing.uploadDate}</td>
                <td><span className={`status-badge ${drawing.priority === "High" ? "pending" : ""}`}>{drawing.priority}</span></td>
                <td>{drawing.files.length} file(s)</td>
                <td>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <ActionButton onClick={() => handleViewDrawingDetails(drawing)} type="view" icon={Eye} tooltip="View" />
                    <ActionButton onClick={() => handleApproveDrawing(drawing.id)} type="approve" icon={Check} tooltip="Approve" />
                    <ActionButton onClick={() => handleRejectDrawing(drawing.id)} type="reject" icon={X} tooltip="Reject" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Drawing Details Modal */}
      {showDrawingDetails && selectedDrawing && (
        <div className="modal-overlay" onClick={() => setShowDrawingDetails(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>Drawing Details - {selectedDrawing.drawingNumber}</h2><button className="modal-close" onClick={() => setShowDrawingDetails(false)}>×</button></div>
            <div className="modal-body">
              <div className="details-grid">
                <div className="detail-item"><label>Customer:</label><span>{selectedDrawing.customer}</span></div>
                <div className="detail-item"><label>Drawing Number:</label><span>{selectedDrawing.drawingNumber}</span></div>
                <div className="detail-item"><label>Title:</label><span>{selectedDrawing.title}</span></div>
                <div className="detail-item"><label>Upload Date:</label><span>{selectedDrawing.uploadDate}</span></div>
                <div className="detail-item"><label>Priority:</label><span className={`status-badge ${selectedDrawing.priority === "High" ? "pending" : ""}`}>{selectedDrawing.priority}</span></div>
              </div>
              <div className="items-table"><h3>Attached Files</h3>
                <table><thead><tr><th>File Name</th><th>Size</th><th>Type</th><th>Action</th></tr></thead>
                  <tbody>
                    {selectedDrawing.files.map((file, idx) => (
                      <tr key={idx}><td>{file.name}</td><td>{file.size}</td><td>{file.type}</td><td>
                        <ActionButton onClick={() => handleDownloadFile(file.name)} type="view" icon={Download} tooltip="Download" />
                      </td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-approve" onClick={() => { handleApproveDrawing(selectedDrawing.id); setShowDrawingDetails(false); }}><Check size={16} /> Approve</button>
              <button className="btn-reject" onClick={() => { handleRejectDrawing(selectedDrawing.id); setShowDrawingDetails(false); }}><X size={16} /> Reject</button>
              <button className="btn-cancel" onClick={() => setShowDrawingDetails(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // List of Drawing Content Component
  const ListOfDrawingContent = () => (
    <div className="pending-po-container">
      <div className="summary-cards">
        <div className="summary-card"><div className="summary-icon" style={{ background: "#10B981" }}><FileImage size={24} color="white" /></div><div className="summary-info"><h3>{listOfDrawings.length}</h3><p>Total Drawings</p></div></div>
        <div className="summary-card"><div className="summary-icon" style={{ background: "#4F46E5" }}><Download size={24} color="white" /></div><div className="summary-info"><h3>{listOfDrawings.reduce((sum, d) => sum + d.files.length, 0)}</h3><p>Total Files</p></div></div>
      </div>

      <div className="po-table-container">
        <table className="po-table">
          <thead><tr><th>Customer</th><th>Drawing #</th><th>Title</th><th>Upload Date</th><th>Approval Date</th><th>Files</th><th>Actions</th></tr></thead>
          <tbody>
            {listOfDrawings.map((drawing) => (
              <tr key={drawing.id}>
                <td className="po-number">{drawing.customer}</td><td>{drawing.drawingNumber}</td><td>{drawing.title}</td><td>{drawing.uploadDate}</td><td>{drawing.approvalDate}</td>
                <td>{drawing.files.length} file(s)</td>
                <td>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <ActionButton onClick={() => handleViewDrawingDetails(drawing)} type="view" icon={Eye} tooltip="View" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case "pending-po": return <PendingPOContent />;
      case "edit-request": return <EditRequestContent />;
      case "profit-loss": return <ProfitLossContent />;
      case "pending-drawing": return <PendingDrawingContent />;
      case "list-of-drawing": return <ListOfDrawingContent />;
      default: return <h2 className="md-page-title">Dashboard</h2>;
    }
  };

  return (
    <div className="md-dashboard-container">
      {showLogoutModal && (
        <div className="logout-modal-overlay">
          <div className="logout-modal">
            <div className="logout-modal-header"><div className="logout-modal-icon">👋</div><h3>Confirm Logout</h3></div>
            <div className="logout-modal-body"><p>Are you sure you want to logout from <strong>Managing Director</strong> dashboard?</p><p className="logout-modal-warning">You will be redirected to the login page.</p></div>
            <div className="logout-modal-footer"><button className="logout-modal-cancel" onClick={handleCancelLogout}>Cancel</button><button className="logout-modal-confirm" onClick={handleConfirmLogout}>Yes, Logout</button></div>
          </div>
        </div>
      )}

      <div className="md-sidebar">
        <div className="md-logo-container"><div className="md-logo-icon"><img src="/rahulerp.jpg" alt="Company Logo" className="md-logo-image" /></div><div><h2 className="md-company-name">Ragul Auto Tech</h2><p className="md-company-tagline">AI Powered ERP</p></div></div>
        <nav className="md-nav-menu">
          {tabItems.map((tab) => (<button key={tab.id} className={`md-nav-item ${activeTab === tab.id ? 'md-nav-item-active' : ''}`} onClick={() => setActiveTab(tab.id)}><span className="md-nav-icon">{tab.icon}</span><span>{tab.label}</span></button>))}
        </nav>
        <div className="md-logout-container"><button onClick={handleLogoutClick} className="md-logout-button"><LogOut size={18} /> Logout</button></div>
      </div>

      <div className="md-main-content">
        <ScreenHeader
          title="Managing Director Dashboard"
          description={`Welcome, ${user?.role || 'Managing Director'}`}
          timestamp={new Date().toLocaleString()}
          containerClassName="md-header"
          titleClassName="md-main-title"
          descriptionClassName="md-subtitle"
          actionsClassName="md-header-actions"
          timestampClassName="md-last-updated"
        />

        <div className="md-page-body">
          <div className="md-department-section">
            <h2 className="md-section-title">Department Access</h2>
            <div className="md-department-scroll-container">
              <div className="md-department-horizontal-grid">
                {departmentAccess.map((dept) => (
                  <button
                    key={dept.id}
                    className="md-department-card-horizontal"
                    onClick={() => handleDepartmentClick(dept)}
                    style={{ borderTopColor: dept.color }}
                  >
                    <div className="md-dept-icon-horizontal" style={{ color: dept.color }}>{dept.icon}</div>
                    <div className="md-dept-label-horizontal">{dept.label}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="md-content-card">{renderContent()}</div>
        </div>
      </div>

      {/* Customer Selection Popup */}
      {showCustomerPopup && (
        <div className="modal-overlay" onClick={() => setShowCustomerPopup(false)}>
          <div className="modal-content customer-popup" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Handshake size={20} /> Select Customer</h2>
              <button className="modal-close" onClick={() => setShowCustomerPopup(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="customer-popup-desc">Please select a customer to navigate to their dashboard:</p>
              
              <div className="form-group">
                <label>Choose Customer:</label>
                <select 
                  className="form-input"
                  value={selectedCustomerId}
                  onChange={(e) => setSelectedCustomerId(e.target.value)}
                >
                  <option value="">-- Select Customer --</option>
                  {customers.map(cust => (
                    <option key={cust.id} value={cust.id}>
                      {cust.name} - {cust.contact}
                    </option>
                  ))}
                </select>
              </div>

              {selectedCustomerId && (
                <div className="customer-details-card">
                  <h4>Customer Details</h4>
                  {(() => {
                    const cust = customers.find(c => c.id === selectedCustomerId);
                    return cust ? (
                      <div className="details-grid">
                        <div className="detail-item"><label>Company:</label><span>{cust.name}</span></div>
                        <div className="detail-item"><label>Contact:</label><span>{cust.contact}</span></div>
                        <div className="detail-item"><label>Email:</label><span>{cust.email}</span></div>
                        <div className="detail-item"><label>Phone:</label><span>{cust.phone}</span></div>
                        <div className="detail-item full-width"><label>Address:</label><span>{cust.address}</span></div>
                        <div className="detail-item"><label>GSTIN:</label><span>{cust.gstin}</span></div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowCustomerPopup(false)}>Cancel</button>
              <button className="btn-approve" onClick={handleCustomerSelect}>
                <Handshake size={16} /> Go to Customer Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}